import { useCallback, useEffect, useState } from 'react';
import { AppBar } from '../components/AppBar';
import { QueueStatus } from '../components/QueueStatus';
import { Icon } from '../components/icons';
import { getRouteDetail } from '../api/endpoints';
import { ApiError } from '../api/types';
import { isUnfinished, type RouteDetail, type RouteShipmentInfo, type ShipmentProgress } from '../domain/routes';
import { useT } from '../i18n/I18nProvider';
import type { TKey } from '../i18n/dictionary';

interface Props {
  routeNo: string;
  date: string;
  onBack: () => void;
  onLogout: () => void;
}

const PROGRESS_LABEL: Record<ShipmentProgress, TKey> = {
  LOADED: 'sh.loaded',
  NOT_LOADED: 'sh.notLoaded',
  INCOMPLETE: 'sh.incomplete',
  UNKNOWN: 'sh.unknown',
};

/**
 * Detail trasy — tabulka zásilek s rozskenovaností a naložením.
 *
 * Nedokončené jsou nahoře (řadí `sortForMaster` v mapperu) a filtr na ně je
 * zapnutý ve výchozím stavu: mistr přišel řešit problém, ne číst inventuru.
 */
export function RouteDetailScreen({ routeNo, date, onBack, onLogout }: Props) {
  const { t } = useT();
  const [detail, setDetail] = useState<RouteDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [onlyUnfinished, setOnlyUnfinished] = useState(true);

  const load = useCallback(async () => {
    setDetail(null);
    setError(null);
    try {
      setDetail(await getRouteDetail(routeNo, date));
    } catch (err) {
      console.error('[routes] načtení trasy selhalo:', err);
      setError(err instanceof ApiError && err.kind === 'FORBIDDEN' ? t('routesForbidden') : t('routeErr'));
    }
  }, [routeNo, date, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const shipments = (detail?.shipments ?? []).filter((s) => !onlyUnfinished || isUnfinished(s));

  return (
    <div className="app">
      <AppBar onLogout={onLogout} />

      <div className="screen-pad">
        <QueueStatus />

        <div className="rt-head">
          <h2 className="h2">
            {t('routeTitle')} {routeNo}
          </h2>
          <button className="btn ghost" type="button" onClick={onBack}>
            {t('routeBack')}
          </button>
        </div>

        {detail && (
          <div className="pick rt-pick">
            <div className="prow">
              <div className="pcol">
                <span className="k">{t('driver')}</span>
                <span className="pv">{detail.driverName ?? t('routeNoDriver')}</span>
              </div>
              <div className="pcol rt">
                <span className="k">{t('routeLoaded')}</span>
                <span className="pv num">
                  {detail.totals.loaded} / {detail.totals.parcels}
                </span>
              </div>
            </div>
            {(detail.vehicle || detail.plate) && (
              <div className="veh">
                <span className="k">{t('vehicle')}</span>
                <span className="pv">{detail.vehicle ?? '—'}</span>
                {detail.plate && <span className="plate">{detail.plate}</span>}
              </div>
            )}
          </div>
        )}

        <div className="langs rt-filter" role="group" aria-label={t('routesUnfinishedCount')}>
          <button type="button" aria-pressed={onlyUnfinished} onClick={() => setOnlyUnfinished(true)}>
            {t('routesUnfinishedOnly')}
            {detail ? ` (${detail.totals.unfinished})` : ''}
          </button>
          <button type="button" aria-pressed={!onlyUnfinished} onClick={() => setOnlyUnfinished(false)}>
            {t('routesAll')}
            {detail ? ` (${detail.totals.shipments})` : ''}
          </button>
        </div>

        {error && <p className="status err">{error}</p>}
        {!error && detail === null && <p className="status">{t('routesLoading')}</p>}
        {!error && detail !== null && shipments.length === 0 && (
          <p className="status">{onlyUnfinished ? t('routeNoUnfinished') : t('routesEmpty')}</p>
        )}

        {shipments.map((s) => (
          <ShipmentRow key={s.shipmentId} shipment={s} label={t(PROGRESS_LABEL[s.progress])} />
        ))}
      </div>
    </div>
  );
}

function ShipmentRow({ shipment, label }: { shipment: RouteShipmentInfo; label: string }) {
  const { t } = useT();
  const missing = shipment.parcels.filter((p) => p.whScannedAt === null && p.loadedAt === null);

  return (
    <div className={`sh sh-${shipment.progress.toLowerCase()}`}>
      <div className="sh-top">
        {shipment.sequence !== null && <span className="sh-seq">{shipment.sequence}</span>}
        <div className="sh-id">
          <span className="id mono">{shipment.shipmentNumber}</span>
          {shipment.recipientName && <span className="who">{shipment.recipientName}</span>}
          {shipment.address && <span className="who-sub">{shipment.address}</span>}
          {shipment.sender && (
            <span className="who-sub">
              {t('shSender')}: {shipment.sender}
            </span>
          )}
        </div>
      </div>

      <div className="sh-state">
        <Icon name={shipment.progress === 'LOADED' ? 'check' : shipment.progress === 'UNKNOWN' ? 'quest' : 'warn'} />
        <span>{label}</span>
      </div>

      <div className="sh-nums">
        <span>
          <span className="k">{t('routeScanned')}</span>
          <b>
            {shipment.scannedCount} / {shipment.parcelCount}
          </b>
        </span>
        <span>
          <span className="k">{t('routeLoaded')}</span>
          <b>
            {shipment.loadedCount} / {shipment.parcelCount}
          </b>
        </span>
        {shipment.weightKg !== null && (
          <span>
            <span className="k">{t('weight')}</span>
            <b>{shipment.weightKg} kg</b>
          </span>
        )}
      </div>

      {missing.length > 0 && (
        <div className="sh-missing">
          {t('shMissing')}: {missing.map((p) => p.packageNumber).join(', ')}
        </div>
      )}
    </div>
  );
}
